import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { QuoteDto, QuoteRevisionDto } from "@grizzly/shared";
import { quoteStatus } from "@grizzly/shared";
import { api, quotePdfUrl } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatAmount } from "../lib/format.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { ErrorState, Skeleton } from "../components/ui/states.js";
import { Modal } from "../components/ui/modal.js";
import { TextInput } from "../components/ui/field.js";
import { QuoteLines } from "../components/sales/quoteLines.js";
import { useToast } from "../lib/useToast.js";
import { ArrowLeft, FileText } from "lucide-react";

export function QuoteDetailPage() {
  const { id = "" } = useParams();
  const { push } = useToast();
  const { t } = useI18n();
  const [quote, setQuote] = useState<QuoteDto | null>(null);
  const [revision, setRevision] = useState<QuoteRevisionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string } | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getQuote(id);
      setQuote(res.quote);
      setRevision(res.revision);
    } catch (err) {
      setError({ message: (err as ApiClientError).message });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, success: string) => {
      setBusy(true);
      try {
        await fn();
        push(success);
        await load();
      } catch (err) {
        const e = err as ApiClientError;
        push(e.message, e.requestId ? `Request ID ${e.requestId}` : undefined, "danger");
      } finally {
        setBusy(false);
      }
    },
    [load, push]
  );

  const decline = useCallback(async () => {
    setBusy(true);
    try {
      await api.declineQuote(id, declineReason || undefined);
      push(t("sales.declinedToast"));
      setDeclineOpen(false);
      setDeclineReason("");
      await load();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [declineReason, id, load, push, t]);

  if (loading) return <Skeleton rows={6} />;
  if (error || !quote)
    return <ErrorState message={error?.message ?? t("sales.notFound")} />;

  const canSend = revision?.status === "draft";
  const canAccept = quote.status === "sent";
  const canRevise = quote.status === "sent";
  const canDecline = quote.status === "sent" || quote.status === "draft";

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/quotes">
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
            {t("sales.backToQuotes")}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={t("sales.quoteTitle", { number: quote.number })}
        description={`${quote.customerName}${revision ? ` · ${t("sales.revisionNo", { no: revision.revisionNo })}` : ""}`}
        actions={
          <>
            <Button variant="secondary" asChild>
              <a href={quotePdfUrl(quote.id)} target="_blank" rel="noreferrer">
                <FileText className="h-4 w-4" aria-hidden="true" />
                PDF
              </a>
            </Button>
            {canSend ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(() => api.sendQuote(quote.id), t("sales.sentToast"))
                }
              >
                {t("sales.send")}
              </Button>
            ) : null}
            {canRevise ? (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  void act(() => api.reviseQuote(quote.id), t("sales.reviseToast"))
                }
              >
                {t("sales.revise")}
              </Button>
            ) : null}
            {canAccept ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(() => api.acceptQuote(quote.id), t("sales.acceptToast"))
                }
              >
                {t("sales.accept")}
              </Button>
            ) : null}
            {canDecline ? (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => setDeclineOpen(true)}
              >
                {t("sales.decline")}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex items-center gap-2 text-sm text-ink-muted">
        <Badge tone={quoteStatus(quote.status).tone}>
          {t(`status.quote.${quote.status}` as MessageKey)}
        </Badge>
        {quote.validUntil ? (
          <span>{t("sales.validUntil", { date: quote.validUntil })}</span>
        ) : null}
        {quote.leadTimeDays !== null && quote.leadTimeDays !== undefined ? (
          <span>{t("sales.leadTime", { days: quote.leadTimeDays })}</span>
        ) : null}
      </div>

      <Card>
        <QuoteLines
          quoteId={quote.id}
          revision={revision}
          onChanged={() => void load()}
        />
        {revision ? (
          <div className="mt-4 flex items-center justify-end gap-2 border-t border-line pt-3">
            <span className="text-sm text-ink-muted">{t("common.fields.total")}</span>
            <span className="tabular text-lg font-semibold text-ink">
              {formatAmount(revision.totalMinor)}
            </span>
          </div>
        ) : null}
      </Card>

      <Modal
        open={declineOpen}
        onOpenChange={setDeclineOpen}
        title={t("sales.declineTitle")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeclineOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" disabled={busy} onClick={decline}>
              {t("sales.declineConfirm")}
            </Button>
          </>
        }
      >
        <TextInput
          label={t("sales.reasonOptional")}
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
        />
      </Modal>
    </>
  );
}
